import { pathOr, path, find, propEq, isNil, isEmpty } from 'ramda'
import { constants as c } from 'mage-common'
import moment from 'moment-timezone'

export const CONSTANTS = {
  MEASURES: {
    orderCount: 'orderCount',
    orderVolume: 'orderVolume',
    orderVolumeAverage: 'orderVolumeAverage'
  },
  DERIVATION_TYPES: {
    currency: 'currency'
  },
  COMPARISONS: {
    sevenDaysAgo: 'seven_days_ago'
  },
  CUBES: {
    orderStream: 'order-stream'
  },
  RESOLUTION: {
    day: 'day',
    hour: 'hour'
  },
  CURRENCIES: {
    EUR: 'EUR',
    AUD: 'AUD',
    USD: 'USD',
    SEK: 'SEK',
    NOK: 'NOK',
    DKK: 'DKK',
    GBP: 'GBP',
    CAD: 'CAD'
  },
  NO_LEVEL: 'noLevel',
  ALL_MIDS: 'all_mids',
  ALL_COUNTRIES: 'all_countries',
  ALL_CURRENCIES: 'all_currencies'
}

const defaultValue = '-'

export class OlapResponse {
  static getDataPoints (response = {}) {
    return pathOr([], ['data', 'dataPoints'], response)
  }

  static getMeasureData (response = {}, measure) {
    const measures = pathOr([], ['data', 'measures'], response)
    return find(propEq('measure', measure))(measures) || {}
  }

  static getTotalForMeasure (response = {}, measure) {
    const measureData = this.getMeasureData(response, measure)
    return !isEmpty(measureData) && !isNil(measureData) && !isNil(measureData.total)
      ? measureData.total
      : defaultValue
  }

  static getCompareValueForMeasure (response = {}, measure, comparison) {
    const measureData = this.getMeasureData(response, measure)
    return pathOr(defaultValue, ['compare', comparison, 'value'], measureData)
  }

  static getCompareRatioForMeasure (response = {}, measure, comparison) {
    const measureData = this.getMeasureData(response, measure)
    return pathOr(defaultValue, ['compare', comparison, 'ratio'], measureData)
  }

  static getDataPointsForMeasure (response = {}, measure) {
    const data = pathOr([], ['data', 'dataPoints'], response)
    return data.map((dp) => {
      return ({
        key: dp.key,
        value: pathOr(null, ['value'], find(propEq('measure', measure))(dp.measures))
      })
    })
  }
}

export class OlapRequest {
  static getSlice (config, level) {
    const slices = config.slices || []
    const slice = slices.find(item => item.level === level)

    return path(['eq', 0], slice)
  }

  static buildSlices ({ brandId, dateRange: { from, to, timezone = 'UTC' } = {}, currency, billingCountry, merchantIds, resolution = 'day' }) {
    const slices = []
    if (from > to || (from && !to) || (!from && to)) {
      throw new Error('Date range is not correct')
    }
    if (brandId) {
      slices.push({ level: 'brandId', eq: [brandId] })
    }
    if (from && to) {
      slices.push({ level: resolution, from, to, ...(timezone && { timezone: timezone }) })
    }
    if (currency && currency !== CONSTANTS.ALL_CURRENCIES) {
      slices.push({ level: 'currency', eq: [currency] })
    }
    if (billingCountry && billingCountry !== CONSTANTS.ALL_COUNTRIES) {
      slices.push({ level: 'billingCountryCode', eq: [billingCountry] })
    }
    if (merchantIds && !merchantIds.includes(CONSTANTS.ALL_MIDS)) {
      slices.push({ level: 'merchantIds', eq: [].concat(merchantIds) })
    }

    return slices
  }

  static getLevels ({ level, resolution }) {
    const levels = []

    if (resolution) {
      levels.push(resolution)
    }

    if (level && level !== CONSTANTS.NO_LEVEL) {
      levels.push(level)
    }

    return levels
  }
}

export const getTimezone = () => moment.tz.guess()

export const getDefaultCurrencyForRegionAndTimezone = ({ region, timezone }) => {
  switch (region) {
    case c.REGIONS.OC:
      return CONSTANTS.CURRENCIES.AUD
    case c.REGIONS.US:
      if (moment.tz.zonesForCountry('CA').includes(timezone)) {
        return CONSTANTS.CURRENCIES.CAD
      }
      return CONSTANTS.CURRENCIES.USD
    case c.REGIONS.EU:
      switch (timezone) {
        case 'Europe/Stockholm':
          return CONSTANTS.CURRENCIES.SEK
        case 'Europe/Oslo':
          return CONSTANTS.CURRENCIES.NOK
        case 'Europe/Copenhagen':
          return CONSTANTS.CURRENCIES.DKK
        case 'Europe/London':
          return CONSTANTS.CURRENCIES.GBP
        default:
          return CONSTANTS.CURRENCIES.EUR
      }
    default:
      return CONSTANTS.CURRENCIES.EUR
  }
}

const getMoment = date => {
  if (!date || !moment(date).isValid()) {
    throw new Error('Invalid date provided')
  }
  return moment(date)
}

const getStartOfDay = date => (
  getMoment(date)
    .startOf('day')
    .startOf('hour')
    .startOf('minute')
    .startOf('second')
    .format('YYYY-MM-DDTHH:mm:ss.SSS')
)

const getEndOfDay = date => (
  getMoment(date)
    .endOf('day')
    .endOf('hour')
    .endOf('minute')
    .endOf('second')
    .format('YYYY-MM-DDTHH:mm:ss.SSS')
)

const getPrevDay = date => getStartOfDay(getMoment(date).subtract(1, 'day'))
const getNextDay = date => getStartOfDay(getMoment(date).add(1, 'day'))
const getDayOfWeek = date => getMoment(date).isoWeekday()

const isTodayOrAfter = date => getMoment(date).isSameOrAfter(moment(), 'day')
const isYesterday = date => getMoment(date).isSame(moment().subtract(1, 'day'), 'day')
const isOneWeekAgoOrBefore = date => getMoment(date).isSameOrBefore(moment().subtract(1, 'week'), 'day')

const getFullDayRange = ({ date, timezone }) => {
  return getTimeRangeBetweenDates({ from: date, to: date, timezone })
}

export const getTimeRangeBetweenDates = ({ from, to, timezone }) => {
  if (getMoment(to).isBefore(getMoment(from))) {
    throw new Error('Invalid date provided')
  }
  if (!timezone) {
    throw new Error('No timezone provided')
  }

  return {
    from: getStartOfDay(from),
    to: getEndOfDay(to),
    timezone
  }
}

export const insightsDateUtils = {
  getTimeRangeBetweenDates,
  getFullDayRange,
  getStartOfDay,
  getEndOfDay,
  getPrevDay,
  getNextDay,
  getDayOfWeek,
  isTodayOrAfter,
  isYesterday,
  isOneWeekAgoOrBefore
}
